import { useState } from 'preact/hooks';
import { ErrorBox, Loading, Segmented } from '../components/ui';
import { api, BASE, logout, type Me } from '../lib/api';
import { Link } from '../lib/router';
import { errorText, prefs, setPref, toast, useAsync, type Prefs } from '../lib/store';

interface Token {
  id: string;
  name: string;
  client_id: string;
  prefix: string;
  scopes: string[];
  drafts_only: boolean;
  expires_at: string | null;
  last_used_at: string | null;
  created_at: string;
  revoked_at: string | null;
}

function Tokens() {
  const { data, error, reload } = useAsync(() => api.get<{ items: Token[] }>('/tokens'), []);
  const [name, setName] = useState('ChatGPT');
  const [write, setWrite] = useState(true);
  const [draftsOnly, setDraftsOnly] = useState(false);
  const [days, setDays] = useState('');
  const [created, setCreated] = useState<string | null>(null);

  const create = async (e: Event) => {
    e.preventDefault();
    try {
      const r = await api.post<{ token: string }>('/tokens', {
        name,
        scopes: write ? ['bakery:read', 'bakery:write'] : ['bakery:read'],
        drafts_only: draftsOnly,
        expires_days: days ? Number(days) : null,
      });
      setCreated(r.token);
      reload();
    } catch (err) {
      toast(errorText(err), 'error');
    }
  };

  return (
    <section class="panel section" aria-labelledby="tok-h">
      <h2 id="tok-h">Connected clients & tokens</h2>
      <p class="muted">
        Personal access tokens let a private Custom GPT (or a script) use Bakery. Paste the token as the Action's Bearer API key and import the schema from{' '}
        <code>{location.origin + BASE}/api/v1/openapi.json</code>. Writes by tokens are flagged “Needs review”, require a change note, and can never delete.
      </p>
      {created && (
        <div class="notice notice-warning" role="alert">
          <p>
            <strong>Copy this token now — it won't be shown again.</strong>
          </p>
          <div class="token-box mono">{created}</div>
          <div class="row" style={{ marginTop: 'var(--bk-space-2)' }}>
            <button class="dr-btn bk-sm" onClick={() => navigator.clipboard?.writeText(created).then(() => toast('Copied'))}>
              Copy
            </button>
            <button class="dr-btn bk-ghost bk-sm" onClick={() => setCreated(null)}>
              Done
            </button>
          </div>
        </div>
      )}
      <form class="field-grid" onSubmit={create} style={{ alignItems: 'end', marginBottom: 'var(--bk-space-4)' }}>
        <div>
          <label for="tn">Name</label>
          <input id="tn" class="dr-input" value={name} onInput={(e) => setName(e.currentTarget.value)} required />
        </div>
        <div>
          <label for="td">Expires after (days)</label>
          <input id="td" class="dr-input" inputMode="numeric" placeholder="never" value={days} onInput={(e) => setDays(e.currentTarget.value)} />
        </div>
        <label class="checkbox">
          <input type="checkbox" checked={write} onChange={(e) => setWrite(e.currentTarget.checked)} /> Can write
        </label>
        <label class="checkbox">
          <input type="checkbox" checked={draftsOnly} onChange={(e) => setDraftsOnly(e.currentTarget.checked)} /> Drafts only
        </label>
        <button class="dr-btn dr-btn--primary">Create token</button>
      </form>
      {error ? <ErrorBox error={error} /> : !data ? <Loading /> : (
        <table class="diff">
          <thead>
            <tr>
              <th>Name</th>
              <th>Scopes</th>
              <th>Last used</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.items.map((t) => (
              <tr>
                <td>
                  {t.name} <span class="mono muted">{t.prefix}…</span>
                  {t.drafts_only && <span class="dr-tag bk-draft">drafts only</span>}
                </td>
                <td class="small">{t.scopes.join(' ')}</td>
                <td class="small muted">{t.revoked_at ? `revoked ${new Date(t.revoked_at).toLocaleDateString()}` : t.last_used_at ? new Date(t.last_used_at).toLocaleString() : 'never'}</td>
                <td>
                  {!t.revoked_at && (
                    <button
                      class="dr-btn bk-danger bk-sm"
                      onClick={async () => {
                        if (!confirm(`Revoke “${t.name}”? Clients using it stop working immediately.`)) return;
                        await api.del(`/tokens/${t.id}`);
                        reload();
                      }}
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function Security({ me }: { me: Me }) {
  return (
    <section class="panel section" aria-labelledby="sec-h">
      <h2 id="sec-h">Sign-in</h2>
      <p>
        You sign in with <strong>Identity DHM</strong>
        {me.email ? (
          <>
            {' '}
            as <strong>{me.email}</strong>
          </>
        ) : null}
        . Passkeys, two-factor, your password and active sessions are managed there.
      </p>
      {me.account_url && (
        <a class="dr-btn" href={me.account_url} target="_blank" rel="noopener">
          Manage account in Identity DHM
        </a>
      )}
      <p class="muted small" style={{ marginTop: 'var(--bk-space-3)' }}>
        Signing out of Bakery also signs you out of Identity DHM and your other DigitalHeMan apps.
      </p>
    </section>
  );
}

function Tags() {
  const { data, reload } = useAsync(() => api.get<{ items: { id: number; name: string; facet: string | null; count: number }[] }>('/tags'), []);
  const rename = async (id: number, current: string) => {
    const name = prompt(`Rename “${current}” to (an existing tag name merges them):`, current);
    if (!name || name === current) return;
    try {
      await api.patch(`/tags/${id}`, { name });
      toast('Tag updated');
      reload();
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };
  return (
    <section class="panel section" aria-labelledby="tags-h">
      <h2 id="tags-h">Tags</h2>
      <p class="muted small">Click a tag to rename it; renaming onto an existing tag merges the two.</p>
      <div class="chips">
        {data?.items.map((t) => (
          <button class="dr-tag" onClick={() => rename(t.id, t.name)}>
            {t.name} <span class="muted">{t.count}</span>
            {t.facet && <span class="muted small">· {t.facet}</span>}
          </button>
        ))}
      </div>
    </section>
  );
}

function Audit() {
  const [open, setOpen] = useState(false);
  const { data } = useAsync(() => (open ? api.get<{ items: any[] }>('/audit?limit=100') : Promise.resolve(null)), [open]);
  return (
    <section class="panel section">
      <details onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}>
        <summary>
          <strong>Audit log</strong>
        </summary>
        {data && (
          <table class="diff">
            <tbody>
              {data.items.map((a) => (
                <tr>
                  <td class="small muted">{new Date(a.at).toLocaleString()}</td>
                  <td>{a.actor}</td>
                  <td>{a.action}</td>
                  <td class="small">{a.card_id ? <Link href={`/c/${a.card_id}`}>card</Link> : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </details>
    </section>
  );
}

export function Settings({ me }: { me: Me }) {
  const p = prefs.use();
  return (
    <>
      <div class="row-between">
        <h1>Settings</h1>
        <span class="row">
          <span class="muted">Signed in as {me.name ?? me.email}</span>
          <button class="dr-btn bk-ghost" onClick={() => void logout()}>
            Sign out
          </button>
        </span>
      </div>

      <section class="panel section" aria-labelledby="disp-h">
        <h2 id="disp-h">Display (this device)</h2>
        <div class="stack">
          <div class="row">
            <span class="grow">Units</span>
            <Segmented<Prefs['units']> label="Units" value={p.units} onChange={(v) => setPref('units', v)} options={[['as_written', 'As written'], ['metric', 'Metric'], ['us', 'US']]} />
          </div>
          <div class="row">
            <span class="grow">Theme</span>
            <Segmented<Prefs['theme']> label="Theme" value={p.theme} onChange={(v) => setPref('theme', v)} options={[['dark', 'Dark'], ['light', 'Light']]} />
          </div>
          <div class="row">
            <span class="grow">Bake mode theme</span>
            <Segmented<Prefs['cookTheme']> label="Bake mode theme" value={p.cookTheme} onChange={(v) => setPref('cookTheme', v)} options={[['light', 'Light'], ['dark', 'Dark']]} />
          </div>
          <div class="row">
            <span class="grow">Grid density</span>
            <Segmented<Prefs['density']> label="Density" value={p.density} onChange={(v) => setPref('density', v)} options={[['comfortable', 'Comfortable'], ['compact', 'Compact'], ['list', 'List']]} />
          </div>
        </div>
      </section>

      <Tokens />

      <section class="panel section" aria-labelledby="mcp-h">
        <h2 id="mcp-h">ChatGPT connector (MCP)</h2>
        <p>
          Status: <strong>{me.mcp_enabled ? 'enabled' : 'disabled until the IAM platform can issue OAuth tokens'}</strong>
        </p>
        <p class="muted small">
          Endpoint: <code>{location.origin + BASE}/mcp</code> · Protected-resource metadata: <code>{location.origin}/.well-known/oauth-protected-resource{BASE}/mcp</code>
        </p>
      </section>

      <Security me={me} />
      <Tags />

      <section class="panel section" aria-labelledby="data-h">
        <h2 id="data-h">Your data</h2>
        <p class="muted">
          The export is a ZIP with <code>bakery.json</code>, full revision history, media, and one Markdown file per card (Obsidian-friendly). Import it again from Capture → Import file.
        </p>
        <div class="row">
          <a class="dr-btn" href={`${BASE}/api/v1/export`} download>
            Download export
          </a>
          <Link class="dr-btn" href="/capture">
            Import…
          </Link>
          <Link class="dr-btn bk-ghost" href="/trash">
            Trash
          </Link>
        </div>
      </section>

      <Audit />
    </>
  );
}
